import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { aboutInfo } from '@/mocks/articles';

export default function About() {
  const [formStatus, setFormStatus] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <Breadcrumb />

      <main>
        {/* Brand Introduction */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading text-foreground-950 text-center">
              {aboutInfo.title}
            </h1>
            <div className="mt-10 space-y-6 text-sm text-foreground-700 leading-relaxed">
              <p>{aboutInfo.intro}</p>
              <p>{aboutInfo.mission}</p>
              <p>{aboutInfo.history}</p>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-14 md:py-18 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-950 text-center mb-10">
              我们的价值观
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {aboutInfo.values.map((value, index) => (
                <div
                  key={index}
                  className="bg-background-50 border border-background-200/70 rounded-lg p-6"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 mb-4">
                    <i className="ri-star-line text-accent-500 text-lg"></i>
                  </div>
                  <h3 className="text-base font-heading text-foreground-950 mb-2">{value.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="py-14 md:py-18 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-950 text-center mb-10">
              学术声誉
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="text-center p-8 bg-background-50 border border-background-200/70 rounded-lg">
                <div className="text-3xl font-heading text-primary-500">50+</div>
                <p className="mt-2 text-sm text-foreground-600">原创分析文章</p>
              </div>
              <div className="text-center p-8 bg-background-50 border border-background-200/70 rounded-lg">
                <div className="text-3xl font-heading text-primary-500">10万+</div>
                <p className="mt-2 text-sm text-foreground-600">累计读者</p>
              </div>
              <div className="text-center p-8 bg-background-50 border border-background-200/70 rounded-lg">
                <div className="text-3xl font-heading text-primary-500">5</div>
                <p className="mt-2 text-sm text-foreground-600">核心研究领域</p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-14 md:py-18 bg-background-100">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-950 text-center mb-3">
              联系我们
            </h2>
            <p className="text-center text-sm text-foreground-600 mb-10">
              如有任何学术合作、投稿意向或问题咨询，请通过以下表单与我们联系
            </p>

            <form
              className="space-y-5"
              data-readdy-form
              action="https://readdy.ai/api/form/d9splb58o1vn02ekujr0"
              method="POST"
              onSubmit={function (e) {
                e.preventDefault();
                const form = e.currentTarget;
                const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement);
                if (honeypot && honeypot.value.trim()) {
                  setFormStatus({ type: 'success', msg: '感谢您的留言，我们会尽快回复！' });
                  return;
                }
                const fd = new FormData(form);
                fd.delete('phone_alt');
                const urlEncoded = new URLSearchParams();
                fd.forEach((v, k) => { urlEncoded.append(k, v as string); });
                fetch(form.action, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: urlEncoded.toString() })
                  .then(async (r) => {
                    const txt = await r.text();
                    let p;
                    try { p = JSON.parse(txt); } catch (_) { p = {}; }
                    const ok = r.ok && (p?.code === 'OK');
                    if (ok) {
                      setFormStatus({ type: 'success', msg: '感谢您的留言，我们会尽快回复！' });
                      form.reset();
                    } else {
                      setFormStatus({ type: 'error', msg: p?.meta?.message || '提交失败，请重试' });
                    }
                  })
                  .catch(() => {
                    setFormStatus({ type: 'error', msg: '网络错误，请稍后重试' });
                  });
              } as unknown as React.FormEventHandler}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-red-400">*</span>
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 text-sm border border-background-300/60 rounded-md bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-red-400">*</span>
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 text-sm border border-background-300/60 rounded-md bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                    placeholder="请输入您的邮箱地址"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  id="contact-subject"
                  type="text"
                  name="subject"
                  className="w-full px-4 py-2.5 text-sm border border-background-300/60 rounded-md bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                  placeholder="请输入留言主题"
                />
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言 <span className="text-red-400">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-2.5 text-sm border border-background-300/60 rounded-md bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all resize-none"
                  placeholder="请输入您的留言内容（最多500字）"
                ></textarea>
              </div>
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="contact-hp"
              />
              <button
                type="submit"
                className="w-full sm:w-auto px-10 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium text-sm rounded-md transition-colors duration-200 whitespace-nowrap"
              >
                发送留言
              </button>
              {formStatus && (
                <p className={`text-sm ${formStatus.type === 'success' ? 'text-green-600' : 'text-red-500'}`}>
                  {formStatus.msg}
                </p>
              )}
            </form>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}